import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Eye, CheckCircle2, XCircle, Clock, ArrowDownToLine,
  ChevronLeft, ChevronRight, Loader2, SlidersHorizontal, RotateCcw, Filter,
} from 'lucide-react';
import { adminAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import ActionMenu from '../../shared/ActionMenu';

/* ── design tokens ── */
const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtINR  = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

const StatusBadge = ({ status }) => {
  const map = {
    pending:   { bg: C.warnSoft,    color: C.warning, label: 'Pending'   },
    approved:  { bg: C.primarySoft, color: C.primary, label: 'Approved'  },
    processed: { bg: C.successSoft, color: C.success, label: 'Processed' },
    rejected:  { bg: C.dangerSoft,  color: C.danger,  label: 'Rejected'  },
    cancelled: { bg: '#F1F5F9',     color: C.muted,   label: 'Cancelled' },
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted, label: status };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: 'capitalize', letterSpacing: .3 }}>{s.label}</span>;
};

const KpiCard = ({ label, value, sub, color, bg, Icon, active, onClick }) => {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)} style={{
      background: C.surface, borderRadius: C.radius,
      border: `1.5px solid ${active ? color : hov ? C.primaryTint : C.border}`,
      padding: '18px 20px', cursor: onClick ? 'pointer' : 'default',
      boxShadow: active ? `0 4px 20px ${color}20` : hov ? '0 4px 16px rgba(20,20,43,0.06)' : '0 1px 4px rgba(20,20,43,0.04)',
      transition: 'all .18s', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: active ? color : C.ink, lineHeight: 1, letterSpacing: '-.02em', minHeight: 30, display: 'flex', alignItems: 'center' }}>{value}</div>
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: active ? color : bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background .18s' }}>
          <Icon size={18} color={active ? '#fff' : color} strokeWidth={2} />
        </div>
      </div>
      {sub && <div style={{ fontSize: 12, color: active ? color : C.muted, fontWeight: 500 }}>{sub}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════ */
const AdminWithdrawals = () => {
  const navigate = useNavigate();
  const [loading,     setLoading]     = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [pagination,  setPagination]  = useState(null);
  const [approving,    setApproving]   = useState(null);
  const [showFilters,  setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '', status: 'pending', date_from: '', date_to: '',
    page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc',
  });
  const [stats, setStats] = useState({ total: 0, pending: 0, processed: 0, rejected: 0, totalAmount: 0, pendingAmount: 0 });

  useEffect(() => { fetchWithdrawals(); }, [filters.page]);

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      const r = await adminAPI.withdrawals.getAll(filters);
      const list = r.data.data.withdrawals;
      setWithdrawals(list);
      setPagination(r.data.data.pagination);
      const pending = list.filter(w => w.status === 'pending');
      setStats({
        total:         list.length,
        pending:       pending.length,
        processed:     list.filter(w => w.status === 'processed').length,
        rejected:      list.filter(w => w.status === 'rejected').length,
        totalAmount:   list.reduce((s, w) => s + parseFloat(w.amount || 0), 0),
        pendingAmount: pending.reduce((s, w) => s + parseFloat(w.amount || 0), 0),
      });
    } catch { toast.error('Failed to fetch withdrawals'); }
    finally { setLoading(false); }
  };

  const setFilter = (k, v) => setFilters(p => ({ ...p, [k]: v, page: 1 }));
  const setPage   = (p)    => setFilters(f => ({ ...f, page: p }));

  const quickApprove = async (id) => {
    setApproving(id);
    try { await adminAPI.withdrawals.approve(id); toast.success('Withdrawal approved'); fetchWithdrawals(); }
    catch (e) { toast.error(e.response?.data?.message || 'Failed to approve'); }
    finally { setApproving(null); }
  };

  const pages = (() => {
    if (!pagination) return [];
    const { last_page: t, current_page: c } = pagination;
    let s = Math.max(1, c - 2), e = Math.min(t, s + 4);
    if (e - s < 4) s = Math.max(1, e - 4);
    return Array.from({ length: e - s + 1 }, (_, i) => s + i);
  })();

  const inp = { height: 38, borderRadius: C.radiusSm, border: `1.5px solid ${C.border}`, padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface, outline: 'none', fontFamily: 'inherit' };

  const qTabs = [
    { label: 'All',       val: 'all',       count: stats.total,     color: C.primary },
    { label: 'Pending',   val: 'pending',   count: stats.pending,   color: C.warning },
    { label: 'Processed', val: 'processed', count: stats.processed, color: C.success },
    { label: 'Rejected',  val: 'rejected',  count: stats.rejected,  color: C.danger  },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        .wd-kpi-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 20px; }
        .wd-adv-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 12px; }
        @media (max-width: 1100px) { .wd-kpi-grid { grid-template-columns: repeat(2, 1fr); } .wd-adv-grid { grid-template-columns: 1fr 1fr; } }
        @media (max-width: 640px)  { .wd-kpi-grid { grid-template-columns: 1fr 1fr; } .wd-adv-grid { grid-template-columns: 1fr; } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 22 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-.02em' }}>Withdrawal Management</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Review and process merchant withdrawal requests.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowFilters(v => !v)}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: showFilters ? C.primarySoft : C.surface, color: showFilters ? C.primary : C.body, border: `1.5px solid ${showFilters ? C.primaryTint : C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <SlidersHorizontal size={14} /> Filters {showFilters ? '▴' : '▾'}
          </button>
          <button onClick={() => { setFilters({ search: '', status: 'pending', date_from: '', date_to: '', page: 1, per_page: 15, sort_by: 'created_at', sort_order: 'desc' }); }}
            style={{ height: 38, padding: '0 14px', borderRadius: C.radiusSm, background: C.surface, color: C.body, border: `1.5px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
            <RotateCcw size={13} /> Reset
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="wd-kpi-grid">
        <KpiCard label="Pending Approval" value={stats.pending}   sub={fmtINR(stats.pendingAmount)} color={C.warning} bg={C.warnSoft}    Icon={Clock}           active={filters.status === 'pending'}   onClick={() => setFilter('status', filters.status === 'pending' ? 'all' : 'pending')} />
        <KpiCard label="Processed"        value={stats.processed} sub="Completed payouts"           color={C.success} bg={C.successSoft}  Icon={CheckCircle2}    active={filters.status === 'processed'} onClick={() => setFilter('status', filters.status === 'processed' ? 'all' : 'processed')} />
        <KpiCard label="Rejected"         value={stats.rejected}  sub="Declined requests"           color={C.danger}  bg={C.dangerSoft}   Icon={XCircle}         active={filters.status === 'rejected'}  onClick={() => setFilter('status', filters.status === 'rejected' ? 'all' : 'rejected')} />
        <KpiCard label="Total Requests"   value={stats.total}     sub={fmtINR(stats.totalAmount)}   color={C.primary} bg={C.primarySoft}  Icon={ArrowDownToLine} active={filters.status === 'all'}       onClick={() => setFilter('status', 'all')} />
      </div>

      {/* Filter Bar */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, borderBottom: `1px solid ${C.borderSoft}` }}>
          <div style={{ position: 'relative', flex: 1, borderRight: `1px solid ${C.borderSoft}` }}>
            <Search size={14} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: C.muted, pointerEvents: 'none' }} />
            <input style={{ ...inp, width: '100%', border: 'none', height: 46, paddingLeft: 38, borderRadius: 0, background: 'transparent' }}
              placeholder="Search by request ID or user…" value={filters.search}
              onChange={e => setFilter('search', e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchWithdrawals()} />
          </div>
          <div style={{ display: 'flex', padding: '0 12px', gap: 4, overflowX: 'auto' }}>
            {qTabs.map(({ label, val, count, color }) => (
              <button key={val} onClick={() => setFilter('status', val)}
                style={{ height: 30, padding: '0 12px', borderRadius: 999, border: `1.5px solid ${filters.status === val ? color : 'transparent'}`, background: filters.status === val ? color : 'transparent', color: filters.status === val ? '#fff' : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 5, transition: 'all .15s' }}>
                {label}
                <span style={{ background: filters.status === val ? 'rgba(255,255,255,0.25)' : C.borderSoft, color: filters.status === val ? '#fff' : C.muted, fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>{count}</span>
              </button>
            ))}
          </div>
          <div style={{ padding: '0 12px', borderLeft: `1px solid ${C.borderSoft}` }}>
            <button onClick={fetchWithdrawals} style={{ height: 36, padding: '0 18px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Filter size={13} /> Apply
            </button>
          </div>
        </div>
        {showFilters && (
          <div style={{ padding: '14px 16px', background: C.bg }}>
            <div className="wd-adv-grid">
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.muted, fontWeight: 600, pointerEvents: 'none' }}>FROM</span>
                <input type="date" style={{ ...inp, paddingLeft: 52, width: '100%' }} value={filters.date_from} onChange={e => setFilter('date_from', e.target.value)} />
              </div>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: C.muted, fontWeight: 600, pointerEvents: 'none' }}>TO</span>
                <input type="date" style={{ ...inp, paddingLeft: 30, width: '100%' }} value={filters.date_to} onChange={e => setFilter('date_to', e.target.value)} />
              </div>
              <select style={{ ...inp, cursor: 'pointer', width: '100%' }} value={filters.sort_order} onChange={e => setFilter('sort_order', e.target.value)}>
                <option value="desc">↓ Newest First</option>
                <option value="asc">↑ Oldest First</option>
              </select>
              <select style={{ ...inp, cursor: 'pointer', width: '100%' }} value={filters.per_page} onChange={e => setFilter('per_page', parseInt(e.target.value))}>
                <option value={15}>15 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
          </div>
        ) : withdrawals.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <ArrowDownToLine size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>No withdrawal requests found.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {['Request ID', 'User', 'Amount', 'Bank Details', 'Status', 'Requested', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map((w) => (
                    <tr key={w.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, transition: 'background .12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = C.primarySoft}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, fontFamily: 'Menlo,Consolas,monospace', fontWeight: 700, color: C.primary }}>{w.request_id}</code>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: C.ink }}>{w.user?.username}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{w.user?.email}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 800, color: C.ink, fontSize: 14 }}>{fmtINR(w.amount)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 600, color: C.ink, fontSize: 12 }}>{w.bank_name}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{w.bank_account_number?.replace(/\d(?=\d{4})/g, '*')}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{w.bank_ifsc}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={w.status} /></td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 12, color: C.ink }}>{fmtDate(w.created_at)}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>{fmtTime(w.created_at)}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <ActionMenu items={[
                          { label: 'View Details', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => navigate(`/admin/withdrawals/${w.id}`) },
                          { label: 'Quick Approve', Icon: CheckCircle2, color: C.success, bg: C.successSoft, loading: approving === w.id, onClick: () => quickApprove(w.id), hidden: w.status !== 'pending' },
                          { label: 'Reject', Icon: XCircle, color: C.danger, bg: C.dangerSoft, onClick: () => navigate(`/admin/withdrawals/${w.id}`), hidden: w.status !== 'pending' },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination && pagination.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: `1px solid ${C.borderSoft}` }}>
                <span style={{ fontSize: 12, color: C.muted }}>Showing {pagination.from}–{pagination.to} of {pagination.total} requests</span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button onClick={() => setPage(pagination.current_page - 1)} disabled={pagination.current_page === 1}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronLeft size={14} />
                  </button>
                  {pages.map(p => (
                    <button key={p} onClick={() => setPage(p)} style={{
                      width: 30, height: 30, borderRadius: 6,
                      border: `1px solid ${p === pagination.current_page ? C.primary : C.border}`,
                      background: p === pagination.current_page ? C.primary : C.surface,
                      color: p === pagination.current_page ? '#fff' : C.body,
                      fontWeight: p === pagination.current_page ? 700 : 400, fontSize: 12, cursor: 'pointer',
                    }}>{p}</button>
                  ))}
                  <button onClick={() => setPage(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page}
                    style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminWithdrawals;
